import { useState, useCallback, useRef, useEffect } from "react";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { FileDropZone, ProcessingState, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { loadPdfDocument, renderPageToJpegBlob, pdfBytesToBlob } from "./utils";
import { findTextLines, type TextLine } from "./textLines";
import { Type, TextCursorInput, Square, Circle, Pencil, Trash2, ChevronLeft, ChevronRight, X, MousePointer2, Undo2, Redo2, GripVertical } from "lucide-react";
import {
  EDITOR_COLORS, DEFAULT_COLORS, TEXT_SIZES, STROKE_WIDTHS, shiftBox,
  newHistory, pushHistory, replacePresent, undoHistory, redoHistory, type History,
} from "./editorStyle";
import { NoticeBox } from "@/components/NoticeBox";
import { useRevealDownload } from "@/hooks/useRevealDownload";
import { parsePdfFormFields, type ParsedFillableForm } from "./formFill";
import { NOT_READABLE_PDF_TITLE, notReadableDetail, isPasswordError } from "./pdfLoadError";
import { exportEditedPdf, isEmptyItem, type EditorAnnotation, type ExportResult } from "./editorExport";

const TOOL_INFO_CONTENT = {
  what: `Adds text, freehand drawing, shapes (rectangle and ellipse outlines), and highlight boxes directly onto a PDF's pages, without needing desktop software like Acrobat — for filling in a blank, labeling something, sketching an arrow or circle to point something out, or drawing attention to a section.\n\nHonestly: this is annotation, not true document editing — it adds new content on top of the page. Edit text mode can replace a single line by covering it with a white patch, but it can't reflow paragraphs or change the PDF's own text layer; for full rewrites, PDF to Word converts to an editable document instead.`,
  how: `Upload your PDF, pick Text, Highlight, Rectangle, Ellipse, or Freehand mode. In Text mode, click anywhere to place a text box and type directly into it. In Highlight/Rectangle/Ellipse mode, click and drag across the area to mark it — you'll see the shape take form as you drag. In Freehand mode, click and drag to sketch a line — release to finish that stroke, and start a new drag for another one. If something's in the wrong spot, remove it with the X and try again. Download when you're done.`,
  tip: `For filling out a form with defined fields, check whether the PDF already has real fillable form fields first — those give cleaner, better-aligned results than manually placing text boxes.`,
};

type Mode = "select" | "edit" | "text" | "box" | "rectangle" | "ellipse" | "freehand";
type Annotation = EditorAnnotation;

/** Form fields people can type into here (signature and read-only fields are left alone). */
const isFillable = (f: { kind: string; readOnly?: boolean }) => !f.readOnly && (f.kind === "text" || f.kind === "checkbox" || f.kind === "dropdown" || f.kind === "radio");

let idCounter = 0;

const DEFAULT_BOX_WIDTH = 0.15;
const DEFAULT_BOX_HEIGHT = 0.05;
const MIN_DRAG_FRAC = 0.015; // below this, treat as a simple click rather than a drag

export default function PdfEditor() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(null);
  const [textLines, setTextLines] = useState<TextLine[] | null>(null);
  const [mode, setMode] = useState<Mode>("text");
  const [hist, setHist] = useState<History<Annotation[]>>(newHistory<Annotation[]>([]));
  const annotations = hist.present;
  // Every change goes through commit() so Undo / Redo can step back through it.
  const commit = (fn: (prev: Annotation[]) => Annotation[]) => setHist((h) => pushHistory(h, fn(h.present)));
  const setAnnotations = commit;
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [colors, setColors] = useState<Record<string, string>>({ ...DEFAULT_COLORS });
  const [strokeW, setStrokeW] = useState(2);
  const [textSize, setTextSize] = useState(14);
  const [bold, setBold] = useState(false);
  const [fill, setFill] = useState(false);
  const [pageWidthPt, setPageWidthPt] = useState(600);
  const moveRef = useRef<{ id: number; last: { x: number; y: number }; pushed: boolean } | null>(null);
  const lastTextEdit = useRef<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<ParsedFillableForm | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formChecked, setFormChecked] = useState<Record<string, boolean>>({});
  const [formDirty, setFormDirty] = useState(false);
  const [skipped, setSkipped] = useState<ExportResult["skipped"]>([]);
  const reveal = useRevealDownload(!!result);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [freehandPoints, setFreehandPoints] = useState<{ x: number; y: number }[]>([]);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const pageAspect = useRef(1.414); // page height / width, from the rendered picture

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    let url: string | null = null;
    (async () => {
      let pdf: Awaited<ReturnType<typeof loadPdfDocument>>;
      try {
        pdf = await loadPdfDocument(file);
      } catch (err) {
        if (cancelled) return;
        console.error("Couldn't load PDF:", err);
        setLoadError(isPasswordError(err) ? `“${file.name}” is password-protected. Unlock it with Unlock PDF first, then drop the unlocked copy here.` : notReadableDetail(file.name));
        setFile(null);
        return;
      }
      if (cancelled) return;
      setPageCount(pdf.numPages);
      const page = await pdf.getPage(currentPage);
      if (cancelled) return;
      const blob = await renderPageToJpegBlob(page, 1.5, 0.9);
      url = URL.createObjectURL(blob);
      setPageImageUrl(url);
      try {
        const tc = await page.getTextContent();
        if (!cancelled) setTextLines(findTextLines(tc.items as unknown as { str: string; transform: number[]; width: number }[], page.getViewport({ scale: 1 })));
      } catch { if (!cancelled) setTextLines([]); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file, currentPage]);

  // The PDF's own fill-in fields (AcroForm), when it has any, so people can type into them.
  useEffect(() => {
    setForm(null); setFormValues({}); setFormChecked({}); setFormDirty(false);
    if (!file) return;
    let cancelled = false;
    parsePdfFormFields(file).then((f) => {
      if (cancelled || f.hasXfa || !f.fields.some(isFillable)) return;
      setForm(f); setFormValues(f.initialValues); setFormChecked(f.initialChecked);
    }).catch(() => { /* not a form, or unreadable here: the page preview reports real problems */ });
    return () => { cancelled = true; };
  }, [file]);

  const handleFiles = useCallback((files: File[]) => {
    setLoadError(null);
    setTextLines(null);
    setFile(files[0] || null);
    setCurrentPage(1);
    setHist(newHistory<Annotation[]>([]));
    setSelectedId(null);
    setResult(null);
  }, []);

  const selected = annotations.find((a) => a.id === selectedId) || null;
  // The style bar shows the selected item's look, or the look new items will get.
  const shownColor = selected ? selected.color : colors[mode === "select" || mode === "edit" ? "text" : mode];
  const shownStroke = selected ? selected.strokeW : strokeW;
  const shownSize = selected ? selected.size : textSize;
  const shownBold = selected ? !!selected.bold : bold;
  const shownFill = selected ? !!selected.fill : fill;
  const newStyle = (type: Annotation["type"]) => ({ color: colors[type], strokeW, size: textSize, bold, fill: type === "rectangle" || type === "ellipse" ? fill : false });

  const patchStyle = (patch: Partial<Pick<Annotation, "color" | "strokeW" | "size" | "bold" | "fill">>) => {
    if (selected) {
      commit((prev) => prev.map((a) => (a.id === selected.id ? { ...a, ...patch } : a)));
      setResult(null);
    }
    if (patch.color) { const c = patch.color; const t = selected ? selected.type : mode === "select" ? null : mode === "edit" ? "text" : mode; if (t) setColors((m) => ({ ...m, [t]: c })); }
    if (patch.strokeW !== undefined) setStrokeW(patch.strokeW);
    if (patch.size !== undefined) setTextSize(patch.size);
    if (patch.bold !== undefined) setBold(patch.bold);
    if (patch.fill !== undefined) setFill(patch.fill);
  };

  // Picking a drawing tool means "new items now", so the style bar stops showing the old selection.
  const chooseMode = (m: Mode) => { setMode(m); if (m !== "select") setSelectedId(null); };

  const undo = () => { setHist(undoHistory); setSelectedId(null); setResult(null); };
  const redo = () => { setHist(redoHistory); setSelectedId(null); setResult(null); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selectedId !== null) { e.preventDefault(); removeAnnotation(selectedId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const startMove = (id: number, point: { x: number; y: number }) => {
    setSelectedId(id);
    lastTextEdit.current = null;
    moveRef.current = { id, last: point, pushed: false };
  };
  const moveSelectedTo = (point: { x: number; y: number }) => {
    const m = moveRef.current;
    if (!m) return;
    const dx = point.x - m.last.x, dy = point.y - m.last.y;
    if (Math.abs(dx) < 1e-4 && Math.abs(dy) < 1e-4) return;
    m.last = point;
    const apply = m.pushed ? replacePresent : (h: History<Annotation[]>, n: Annotation[]) => pushHistory(h, n); // one undo step per move
    m.pushed = true;
    setHist((h) => apply(h, h.present.map((a) => {
      if (a.id !== m.id) return a;
      if (a.type === "freehand" && a.points) {
        const xs = a.points.map((p) => p.x), ys = a.points.map((p) => p.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        const to = shiftBox(minX, minY, maxX - minX, maxY - minY, dx, dy);
        const sx = to.x - minX, sy = to.y - minY;
        return { ...a, points: a.points.map((p) => ({ x: p.x + sx, y: p.y + sy })) };
      }
      const w = a.type === "text" ? 0.05 : a.widthFrac || DEFAULT_BOX_WIDTH;
      const h2 = a.type === "text" ? 0.02 : a.heightFrac || DEFAULT_BOX_HEIGHT;
      const to = shiftBox(a.xFrac, a.yFrac, w, h2, dx, dy);
      return { ...a, xFrac: to.x, yFrac: to.y };
    })));
    setResult(null);
  };

  const fracFromPoint = (clientX: number, clientY: number) => {
    const rect = canvasWrapRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  };
  // Thin wrapper kept so the existing mouse-event call sites below don't
  // need to change -- fracFromPoint is the one real implementation both
  // mouse and touch paths now share.
  const fracFromEvent = (e: React.MouseEvent<HTMLDivElement>) => fracFromPoint(e.clientX, e.clientY);

  /** Click on a line of the page's own text: its words become an editable text item that covers the original. */
  const editLine = (line: TextLine, index: number) => {
    const key = `${currentPage}:${index}`;
    const existing = annotations.find((a) => a.lineKey === key);
    if (existing) { setSelectedId(existing.id); return; }
    const id = idCounter++;
    lastTextEdit.current = null;
    const pad = 0.004;
    setAnnotations((prev) => [...prev, {
      id, page: currentPage, type: "text", text: line.text, lineKey: key,
      xFrac: line.x, yFrac: line.baseline - (line.size * 0.86) / (pageWidthPt * (pageAspect.current || 1.414)),
      cover: { x: Math.max(0, line.x - pad), y: line.y, w: line.w + pad * 2, h: line.h },
      ...newStyle("text"), size: Math.round(line.size * 10) / 10, color: "#000000",
    }]);
    setSelectedId(id);
    setResult(null);
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Text mode stays exactly as it was: a single click places a text box.
    if (mode === "select") { setSelectedId(null); return; }
    if (mode !== "text") return;
    const { x: xFrac, y: yFrac } = fracFromEvent(e);
    const id = idCounter++;
    lastTextEdit.current = null;
    setAnnotations((prev) => [...prev, { id, page: currentPage, xFrac, yFrac, type: "text", text: "", ...newStyle("text") }]);
    setSelectedId(id);
    setResult(null);
  };

  // Highlight/Rectangle/Ellipse modes all use real click-and-drag
  // instead of a fixed-size shape dropped wherever you click -- you see
  // the shape's actual size and position taking form as you drag, the
  // same way a paint program's shape tool works, rather than guessing
  // where a click will land. Freehand mode uses the same mousedown
  // start, but accumulates every intermediate point during the drag
  // instead of only tracking a start/end rectangle.
  const DRAG_MODES: Mode[] = ["box", "rectangle", "ellipse", "freehand"];

  const startDragAt = (point: { x: number; y: number }) => {
    if (!DRAG_MODES.includes(mode)) return;
    setDragStart(point);
    setDragCurrent(point);
    if (mode === "freehand") setFreehandPoints([point]);
  };
  const moveDragTo = (point: { x: number; y: number }) => {
    if (!DRAG_MODES.includes(mode) || !dragStart) return;
    setDragCurrent(point);
    if (mode === "freehand") setFreehandPoints((prev) => [...prev, point]);
  };

  // One set of Pointer Events on the page wrapper (the <img> underneath is pointer-events-none, so every
  // touch, pen or mouse lands on this wrapper). Pointer Events fire for a finger drag on iPhone and Android
  // where separate mouse/touch handlers were not reliably drawing; touch-action: none on the wrapper keeps
  // the page from scrolling away under the drag. Moving an existing item (startMove) uses the same path.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => startDragAt(fracFromPoint(e.clientX, e.clientY));
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = fracFromPoint(e.clientX, e.clientY);
    if (moveRef.current) return moveSelectedTo(p);
    moveDragTo(p);
  };

  const finishDrag = () => {
    if (moveRef.current) { moveRef.current = null; return; }
    if (!DRAG_MODES.includes(mode) || !dragStart || !dragCurrent) {
      setDragStart(null);
      setDragCurrent(null);
      setFreehandPoints([]);
      return;
    }
    const id = idCounter++;

    if (mode === "freehand") {
      // Needs at least 2 points to be a real stroke, not a stray click.
      if (freehandPoints.length >= 2) {
        setAnnotations((prev) => [...prev, { id, page: currentPage, xFrac: 0, yFrac: 0, type: "freehand", points: freehandPoints, ...newStyle("freehand") }]);
        setSelectedId(id);
        setResult(null);
      }
      setDragStart(null);
      setDragCurrent(null);
      setFreehandPoints([]);
      return;
    }

    const dragWidth = Math.abs(dragCurrent.x - dragStart.x);
    const dragHeight = Math.abs(dragCurrent.y - dragStart.y);
    const shapeType = mode as "box" | "rectangle" | "ellipse";

    if (dragWidth < MIN_DRAG_FRAC && dragHeight < MIN_DRAG_FRAC) {
      // Barely moved -- treat as a quick click, drop a sensible default-sized shape.
      setAnnotations((prev) => [
        ...prev,
        { id, page: currentPage, xFrac: dragStart.x, yFrac: dragStart.y, type: shapeType, widthFrac: DEFAULT_BOX_WIDTH, heightFrac: DEFAULT_BOX_HEIGHT, ...newStyle(shapeType) },
      ]);
      setSelectedId(id);
    } else {
      const xFrac = Math.min(dragStart.x, dragCurrent.x);
      const yFrac = Math.min(dragStart.y, dragCurrent.y);
      setAnnotations((prev) => [
        ...prev,
        { id, page: currentPage, xFrac, yFrac, type: shapeType, widthFrac: dragWidth, heightFrac: dragHeight, ...newStyle(shapeType) },
      ]);
      setSelectedId(id);
    }
    setResult(null);
    setDragStart(null);
    setDragCurrent(null);
  };

  const updateAnnotationText = (id: number, text: string) => {
    const next = (prev: Annotation[]) => prev.map((a) => (a.id === id ? { ...a, text } : a));
    // One undo step per text box, not one per keystroke.
    if (lastTextEdit.current === id) setHist((h) => replacePresent(h, next(h.present)));
    else { lastTextEdit.current = id; commit(next); }
    setResult(null);
  };

  const removeAnnotation = (id: number) => { setAnnotations((prev) => prev.filter((a) => a.id !== id)); setSelectedId((s) => (s === id ? null : s)); setResult(null); };

  const applyChanges = async () => {
    if (!file) return;
    setProcessing(true);
    setSkipped([]);
    try {
      const out = await exportEditedPdf(file, annotations, form ? { values: formValues, checked: formChecked } : undefined);
      if (out.formFailed.length > 0) toast.warning(`${out.formFailed.length} form field${out.formFailed.length === 1 ? "" : "s"} could not be filled`, { description: out.formFailed.slice(0, 3).map((f) => `${f.name}: ${f.reason}`).join("; "), duration: 10000 });
      setSkipped(out.skipped);
      setResult(pdfBytesToBlob(out.bytes));
      if (out.skipped.length > 0) {
        toast.warning(`${out.skipped.length} of your items could not be added to the PDF`, { description: "See the notice above the download button.", duration: 10000 });
      } else {
        toast.success(`Changes applied (${out.written} item${out.written === 1 ? "" : "s"} written). Press Download to save the file.`);
      }
    } catch (err) {
      console.error("PDF edit failed:", err);
      toast.error("Failed to apply changes to this PDF");
    } finally {
      setProcessing(false);
    }
  };

  const emptyCount = annotations.filter(isEmptyItem).length;
  const writableCount = annotations.length - emptyCount;
  const pageAnnotations = annotations.filter((a) => a.page === currentPage);

  return (
    <ToolWithSidebar toolId="pdf-editor" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      {/* Above-fold trust strip -- states plainly that this is
          annotation on top of the page, not true editing of the PDF's
          existing body text, before anyone starts using it. */}
      <div className="mb-4 p-3 rounded-lg border bg-muted/50 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
        <span>🔒 Runs entirely in your browser — never uploaded</span>
        <span>✏️ Adds new content on top of the page — use Edit text to change a line that is already on the page</span>
      </div>
      {!file ? (
        <div className="space-y-3">
          <FileDropZone gateAtDownload accept=".pdf" onFiles={handleFiles} onAttempt={() => setLoadError(null)} onUnreadablePdf={(f) => setLoadError(notReadableDetail(f.name))} label="Drop a PDF to edit" description="Add text, freehand drawing, shapes, and highlights directly on the page" />
          {loadError && (
            <NoticeBox role="alert" className="text-sm">
              <p className="font-medium">{NOT_READABLE_PDF_TITLE}</p>
              <p>{loadError} <a href="/tool/repair-pdf" className="underline">Repair PDF</a></p>
            </NoticeBox>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-card">
            <Button size="sm" variant={mode === "select" ? "default" : "outline"} onClick={() => chooseMode("select")} className="gap-1.5" title="Click an item to select it, drag to move it">
              <MousePointer2 className="h-3.5 w-3.5" /> Select
            </Button>
            <Button size="sm" variant={mode === "edit" ? "default" : "outline"} onClick={() => chooseMode("edit")} className="gap-1.5" title="Click a line of the page's own text to change or delete it">
              <TextCursorInput className="h-3.5 w-3.5" /> Edit text
            </Button>
            <Button size="sm" variant={mode === "text" ? "default" : "outline"} onClick={() => chooseMode("text")} className="gap-1.5">
              <Type className="h-3.5 w-3.5" /> Text
            </Button>
            <Button size="sm" variant={mode === "box" ? "default" : "outline"} onClick={() => chooseMode("box")} className="gap-1.5">
              <Square className="h-3.5 w-3.5" /> Highlight
            </Button>
            <Button size="sm" variant={mode === "rectangle" ? "default" : "outline"} onClick={() => chooseMode("rectangle")} className="gap-1.5">
              <Square className="h-3.5 w-3.5" /> Rectangle
            </Button>
            <Button size="sm" variant={mode === "ellipse" ? "default" : "outline"} onClick={() => chooseMode("ellipse")} className="gap-1.5">
              <Circle className="h-3.5 w-3.5" /> Ellipse
            </Button>
            <Button size="sm" variant={mode === "freehand" ? "default" : "outline"} onClick={() => chooseMode("freehand")} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" /> Freehand
            </Button>
            <div className="flex items-center gap-1 ml-auto">
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!hist.past.length} onClick={undo} aria-label="Undo" title="Undo (Ctrl+Z)">
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!hist.future.length} onClick={redo} aria-label="Redo" title="Redo (Ctrl+Y)">
                <Redo2 className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {currentPage} of {pageCount}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={currentPage >= pageCount} onClick={() => setCurrentPage((p) => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {(() => {
            const t = selected ? selected.type : mode === "select" ? null : mode === "edit" ? "text" : mode;
            if (!t) return <p className="text-xs text-muted-foreground px-3 flex items-center" style={{ minHeight: 54 }}>Click an item on the page to select it — then drag to move it, change its look here, or press Delete.</p>;
            const isText = t === "text";
            const isShape = t === "rectangle" || t === "ellipse";
            return (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3 rounded-lg border bg-card" style={{ minHeight: 54 }} data-testid="style-bar">
                <span className="text-xs text-muted-foreground">{selected ? "Selected item" : "New items"}</span>
                <div className="flex items-center gap-1" role="group" aria-label="Colour">
                  {EDITOR_COLORS.map((c) => (
                    <button
                      key={c.hex}
                      type="button"
                      title={c.name}
                      aria-label={c.name}
                      aria-pressed={shownColor.toLowerCase() === c.hex.toLowerCase()}
                      onClick={() => patchStyle({ color: c.hex })}
                      className={`h-6 w-6 rounded-full border ${shownColor.toLowerCase() === c.hex.toLowerCase() ? "ring-2 ring-primary ring-offset-1" : ""}`}
                      style={{ background: c.hex }}
                    />
                  ))}
                </div>
                {isText && (
                  <>
                    <label className="flex items-center gap-1.5 text-xs">Size
                      <select aria-label="Text size" value={shownSize} onChange={(e) => patchStyle({ size: Number(e.target.value) })} className="border rounded px-1.5 py-1 text-xs bg-background">
                        {(TEXT_SIZES.includes(shownSize) ? TEXT_SIZES : [...TEXT_SIZES, shownSize].sort((a, b) => a - b)).map((n) => <option key={n} value={n}>{n} pt</option>)}
                      </select>
                    </label>
                    <Button size="sm" variant={shownBold ? "default" : "outline"} aria-pressed={shownBold} onClick={() => patchStyle({ bold: !shownBold })} className="h-7 px-2 font-bold">B</Button>
                  </>
                )}
                {(isShape || t === "freehand") && (
                  <label className="flex items-center gap-1.5 text-xs">Line
                    <select aria-label="Line width" value={shownStroke} onChange={(e) => patchStyle({ strokeW: Number(e.target.value) })} className="border rounded px-1.5 py-1 text-xs bg-background">
                      {(STROKE_WIDTHS.includes(shownStroke) ? STROKE_WIDTHS : [...STROKE_WIDTHS, shownStroke].sort((a, b) => a - b)).map((n) => <option key={n} value={n}>{n} pt</option>)}
                    </select>
                  </label>
                )}
                {isShape && (
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={shownFill} onChange={(e) => patchStyle({ fill: e.target.checked })} /> Tint inside
                  </label>
                )}
              </div>
            );
          })()}

          {mode === "edit" && (
            <p className="text-xs text-muted-foreground">Click a line of text on the page, then change or clear it. The new text is drawn on a white patch over the old line, in a standard font. The old words are covered, not deleted — they can still be found by searching the file. To remove sensitive text for good, use Redact PDF.</p>
          )}
          {pageImageUrl && (
            <div
              ref={canvasWrapRef}
              onClick={handleCanvasClick}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={finishDrag}
              onPointerCancel={finishDrag}
              onPointerLeave={finishDrag}
              className="relative border rounded-lg overflow-hidden bg-white mx-auto"
              style={{ maxWidth: "600px", containerType: "inline-size", cursor: mode === "select" || mode === "edit" ? "default" : mode === "text" ? "text" : "crosshair", touchAction: "none" }}
            >
              <img
                src={pageImageUrl}
                alt={`Page ${currentPage}`}
                onLoad={(e) => { setPageWidthPt(e.currentTarget.naturalWidth / 1.5); pageAspect.current = e.currentTarget.naturalHeight / e.currentTarget.naturalWidth; }}
                className="w-full h-auto block pointer-events-none select-none"
              />
              {pageAnnotations.filter((a) => a.cover).map((a) => (
                <div key={`cover-${a.id}`} className="absolute bg-white pointer-events-none" style={{ left: `${a.cover!.x * 100}%`, top: `${a.cover!.y * 100}%`, width: `${a.cover!.w * 100}%`, height: `${a.cover!.h * 100}%` }} />
              ))}
              {mode === "edit" && textLines?.map((l, i) => (
                <button key={`line-${i}`} type="button" aria-label={`Edit: ${l.text}`} title="Click to edit this line"
                  onClick={(e) => { e.stopPropagation(); editLine(l, i); }}
                  className="absolute rounded-sm border border-transparent hover:border-primary hover:bg-primary/10 cursor-text"
                  style={{ left: `${l.x * 100}%`, top: `${l.y * 100}%`, width: `${l.w * 100}%`, height: `${l.h * 100}%` }} />
              ))}
              {mode === "edit" && textLines && textLines.length === 0 && (
                <NoticeBox className="absolute inset-x-0 top-2 mx-auto w-fit bg-background px-3 py-1 text-xs pointer-events-none">No editable text on this page (scanned pages are pictures). Use Text to add new text.</NoticeBox>
              )}
              {mode === "freehand" && freehandPoints.length >= 2 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <polyline
                    points={freehandPoints.map((p) => `${p.x * 100},${p.y * 100}`).join(" ")}
                    fill="none"
                    stroke={colors.freehand}
                    strokeWidth={strokeW * (600 / pageWidthPt)}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
              {dragStart && dragCurrent && mode !== "freehand" && (
                <div
                  className={`absolute pointer-events-none ${mode === "ellipse" ? "rounded-full" : "rounded-sm"}`}
                  style={{
                    left: `${Math.min(dragStart.x, dragCurrent.x) * 100}%`,
                    top: `${Math.min(dragStart.y, dragCurrent.y) * 100}%`,
                    width: `${Math.abs(dragCurrent.x - dragStart.x) * 100}%`,
                    height: `${Math.abs(dragCurrent.y - dragStart.y) * 100}%`,
                    ...(mode === "box"
                      ? { background: colors.box, opacity: 0.45 }
                      : { border: `${Math.max(1, strokeW * (600 / pageWidthPt))}px solid ${colors[mode]}`, background: fill ? `${colors[mode]}33` : undefined }),
                  }}
                />
              )}
              {pageAnnotations.filter((a) => a.type === "freehand" && a.points).length > 0 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {pageAnnotations.filter((a) => a.type === "freehand" && a.points).map((ann) => {
                    const pts = ann.points!.map((p) => `${p.x * 100},${p.y * 100}`).join(" ");
                    const px = ann.strokeW * (600 / pageWidthPt);
                    return (
                      <g key={ann.id}>
                        {ann.id === selectedId && (
                          <polyline points={pts} fill="none" stroke="#3b82f6" strokeOpacity={0.35} strokeWidth={px + 6} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        )}
                        <polyline points={pts} fill="none" stroke={ann.color} strokeWidth={px} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                        {mode === "select" && (
                          <polyline
                            data-testid={`freehand-hit-${ann.id}`}
                            points={pts}
                            fill="none"
                            stroke="transparent"
                            strokeWidth={Math.max(px, 14)}
                            vectorEffect="non-scaling-stroke"
                            style={{ pointerEvents: "stroke", cursor: "move" }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => { e.stopPropagation(); startMove(ann.id, fracFromPoint(e.clientX, e.clientY)); }}
                            onTouchStart={(e) => { e.stopPropagation(); const t = e.touches[0]; startMove(ann.id, fracFromPoint(t.clientX, t.clientY)); }}
                          />
                        )}
                      </g>
                    );
                  })}
                </svg>
              )}
              {pageAnnotations.filter((a) => a.type === "freehand" && a.points && a.points.length > 0).map((ann) => (
                <button
                  key={`remove-${ann.id}`}
                  onClick={(e) => { e.stopPropagation(); removeAnnotation(ann.id); }}
                  className="absolute h-5 w-5 rounded-full bg-white border shadow-sm flex items-center justify-center hover:text-destructive"
                  style={{ left: `${ann.points![0].x * 100}%`, top: `${ann.points![0].y * 100}%`, transform: "translate(-50%, -50%)" }}
                  aria-label="Remove this freehand stroke"
                >
                  <X className="h-3 w-3" />
                </button>
              ))}
              {pageAnnotations.filter((a) => a.type !== "freehand").map((ann) => {
                const isSel = ann.id === selectedId;
                const px = ann.strokeW * (600 / pageWidthPt);
                return (
                <div
                  key={ann.id}
                  data-testid={`ann-${ann.type}-${ann.id}`}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => {
                    if (ann.type === "text") { setSelectedId(ann.id); return; }
                    if (mode === "select") { e.stopPropagation(); startMove(ann.id, fracFromPoint(e.clientX, e.clientY)); }
                  }}
                  onTouchStart={(e) => {
                    if (ann.type === "text" || mode !== "select") return;
                    e.stopPropagation();
                    const t = e.touches[0];
                    startMove(ann.id, fracFromPoint(t.clientX, t.clientY));
                  }}
                  className="absolute"
                  style={
                    ann.type === "text"
                      ? { left: `${ann.xFrac * 100}%`, top: ann.cover ? `calc(${ann.yFrac * 100}% + ${((ann.size * 0.56) / pageWidthPt) * 100}cqw)` : `${ann.yFrac * 100}%` }
                      : {
                          left: `${ann.xFrac * 100}%`,
                          top: `${ann.yFrac * 100}%`,
                          // Width/height must live on THIS absolutely-positioned
                          // element, not a nested static child (a percentage on a
                          // normal-flow child collapses to 0 here).
                          width: `${(ann.widthFrac || DEFAULT_BOX_WIDTH) * 100}%`,
                          height: `${(ann.heightFrac || DEFAULT_BOX_HEIGHT) * 100}%`,
                          cursor: mode === "select" ? "move" : undefined,
                        }
                  }
                >
                  {ann.type === "text" ? (
                    <div className="flex items-center gap-1 -translate-y-1/2">
                      <button
                        type="button"
                        aria-label="Move this text"
                        title="Drag to move"
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startMove(ann.id, fracFromPoint(e.clientX, e.clientY)); }}
                        onTouchStart={(e) => { e.stopPropagation(); const t = e.touches[0]; startMove(ann.id, fracFromPoint(t.clientX, t.clientY)); }}
                        className="text-muted-foreground cursor-move"
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <input
                        autoFocus
                        value={ann.text || ""}
                        onFocus={() => setSelectedId(ann.id)}
                        onChange={(e) => updateAnnotationText(ann.id, e.target.value)}
                        placeholder="Type here..."
                        className={`px-1.5 py-0.5 border rounded bg-white/95 shadow-sm ${isSel ? "border-primary" : "border-border"}`}
                        style={{ color: ann.color === "#FFFFFF" ? "#999" : ann.color, fontWeight: ann.bold ? 700 : 400, fontSize: `${(ann.size / pageWidthPt) * 100}cqw`, width: `${Math.max(8, (ann.text?.length || 8) + 2)}ch`, maxWidth: "70cqw" }}
                      />
                      <button onClick={() => removeAnnotation(ann.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove this text">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative group w-full h-full">
                      <div
                        className={`w-full h-full ${ann.type === "ellipse" ? "rounded-full" : "rounded-sm"} ${isSel ? "outline outline-2 outline-offset-2 outline-blue-500/60" : ""}`}
                        style={
                          ann.type === "box"
                            ? { background: ann.color, opacity: 0.45 }
                            : { border: `${Math.max(1, px)}px solid ${ann.color}`, background: ann.fill ? `${ann.color}33` : undefined }
                        }
                      />
                      <button
                        onClick={() => removeAnnotation(ann.id)}
                        aria-label="Remove this shape"
                        className={`absolute -top-2 -right-2 bg-white rounded-full border shadow-sm transition-opacity ${isSel ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {form && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="form-fields">
              <p className="text-sm font-medium">This PDF has fill-in fields — type into them here</p>
              <div className="grid gap-2 max-h-72 overflow-y-auto pr-1">
                {form.fields.filter(isFillable).map((f) => (
                  <label key={f.name} className="text-xs flex flex-col gap-1">
                    <span className="text-muted-foreground">{f.label}{f.page !== null ? ` (page ${f.page + 1})` : ""}</span>
                    {f.kind === "checkbox" ? (
                      <input type="checkbox" checked={!!formChecked[f.name]} onChange={(e) => { setFormChecked((p) => ({ ...p, [f.name]: e.target.checked })); setFormDirty(true); setResult(null); }} className="h-4 w-4 self-start" />
                    ) : f.kind === "dropdown" || f.kind === "radio" ? (
                      <select value={formValues[f.name] ?? ""} onChange={(e) => { setFormValues((p) => ({ ...p, [f.name]: e.target.value })); setFormDirty(true); setResult(null); }} className="border rounded px-2 py-1 text-sm bg-background">
                        <option value="">—</option>
                        {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.multiline ? (
                      <textarea value={formValues[f.name] ?? ""} maxLength={f.maxLength} onChange={(e) => { setFormValues((p) => ({ ...p, [f.name]: e.target.value })); setFormDirty(true); setResult(null); }} className="border rounded px-2 py-1 text-sm bg-background" rows={2} />
                    ) : (
                      <input type="text" value={formValues[f.name] ?? ""} maxLength={f.maxLength} onChange={(e) => { setFormValues((p) => ({ ...p, [f.name]: e.target.value })); setFormDirty(true); setResult(null); }} className="border rounded px-2 py-1 text-sm bg-background" />
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Need to sign this document? Use <a href="/tool/sign-pdf" className="underline">Sign PDF</a> — it places your signature or initials, and runs in your browser too.
          </p>
          <p className="text-xs text-muted-foreground">
            {writableCount} item{writableCount === 1 ? "" : "s"} added across all pages. Click the page above to add more.
            {emptyCount > 0 && ` ${emptyCount} empty text box${emptyCount === 1 ? "" : "es"} will be left out.`}
          </p>
          {result && skipped.length > 0 && (
            <NoticeBox role="alert" className="text-sm space-y-1">
              <p className="font-medium">{skipped.length} of your {writableCount} items could not be written to the PDF, so the download will not include {skipped.length === 1 ? "it" : "them"}:</p>
              <ul className="list-disc pl-5">
                {skipped.map((k) => <li key={k.id}>{k.type === "box" ? "highlight" : k.type} on page {k.page}: {k.reason}</li>)}
              </ul>
              <p>Remove or redo {skipped.length === 1 ? "that item" : "those items"} and apply again, or download the file knowing {skipped.length === 1 ? "it is" : "they are"} missing.</p>
            </NoticeBox>
          )}

          <div className="flex gap-3">
            {processing ? (
              <ProcessingState message="Applying changes..." />
            ) : result ? (
              <>
                <div ref={reveal.ref} className={reveal.className}><DownloadButton blob={result} fileName={`edited_${file.name}`} label={skipped.length > 0 ? `Download without ${skipped.length === 1 ? "the missing item" : `the ${skipped.length} missing items`}` : "Download"} /></div>
                <Button variant="outline" onClick={() => { setFile(null); setHist(newHistory<Annotation[]>([])); setSelectedId(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={applyChanges} disabled={writableCount === 0 && !formDirty}>Apply changes</Button>
                {annotations.length > 0 && (
                  <Button variant="ghost" onClick={() => { setAnnotations(() => []); setSelectedId(null); }} className="gap-1.5">
                    <Trash2 className="h-3.5 w-3.5" /> Clear all
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </ToolWithSidebar>
  );
}
